import type { CaseInformationResponse } from '@/types/case';
import type { CaseFormFields } from '@/components/contact-center/CaseFormDialog';

export interface PrefillOptions {
  categories: Array<{ code: string; name: string }>;
  caseTypeOptions: Array<{ value: number; label: string }>;
  priorityOptions: Array<{ value: string; label: string }>;
  originationOptions: Array<{ value: string; label: string }>;
}

/**
 * Merge a closed case's data into form fields for a new case, resolving
 * dropdown display names to codes. Call-data fields (phoneNumber,
 * reportedByName, assignedTo, reportingMethod, closeCaseLoop) are kept
 * from the current call context.
 */
export function mergeClosedCaseIntoFormFields(
  closedCase: CaseInformationResponse,
  prevFormFields: CaseFormFields,
  opts: PrefillOptions
): CaseFormFields {
  const cat = opts.categories.find(
    c => c.name === closedCase.category || c.code === closedCase.category
  );
  const ct = opts.caseTypeOptions.find(
    o =>
      o.label === closedCase.caseType?.name ||
      o.value === closedCase.caseType?.id
  );
  const pri = opts.priorityOptions.find(
    o => o.value === closedCase.priority || o.label === closedCase.priority
  );
  const orig = opts.originationOptions.find(
    o =>
      o.value === closedCase.origination || o.label === closedCase.origination
  );

  return {
    ...prevFormFields,
    subject: closedCase.subject || '',
    description: closedCase.description || '',
    notes: closedCase.notes || '',
    category: closedCase.category || '',
    categoryCode: cat?.code ?? '',
    categoryName: cat?.name ?? closedCase.category ?? '',
    categoryPriority: pri?.value ?? closedCase.priority ?? '',
    caseType: ct?.label ?? closedCase.caseType?.name ?? '',
    caseTypeId: ct?.value ?? closedCase.caseType?.id ?? null,
    subCaseType: closedCase.subCaseType || '',
    origination: orig?.value ?? closedCase.origination ?? '',
    resolution: closedCase.resolution || '',
    resolutionType: closedCase.resolutionType || '',
    email: closedCase.email || '',
    // Normalize 0 → null: the backend uses 0 for "not set", but the form
    // dropdown treats any non-null value (including 0) as a selection,
    // which disables reportedByName and phoneNumber.
    employeeId: closedCase.employeeId ? closedCase.employeeId : null,
    reportedByEmployeeId: closedCase.reportedByEmployeeId
      ? closedCase.reportedByEmployeeId
      : null,
    clientContactId: closedCase.clientContactId
      ? String(closedCase.clientContactId)
      : '',
    status: '',
    phoneNumber: prevFormFields.phoneNumber,
    reportedByName: prevFormFields.reportedByName,
    assignedTo: prevFormFields.assignedTo,
    reportingMethod: prevFormFields.reportingMethod,
    closeCaseLoop: prevFormFields.closeCaseLoop,
  };
}
